<template>
  <div class="note-suggested-action-wrap">
    <div class="note-suggested-action-header">
      <div class="suggested-action-header-breadcrumb">
        <span class="shrink-0">{{ actionName }}</span>
        <CompactCard
          v-if="!isCreateCardAction"
          :card-id="actionCardId"
          :title="actionResourceTitle"
        />
      </div>
    </div>
    <div class="note-suggested-action">
      <NoteSuggestedActionDetails
        :action-details="validActionDetails"
        :card-id="actionCardId"
        :action-type="actionType"
      />
    </div>
    <div class="note-suggested-action-footer">
      <ButtonCustom
        v-tooltip="rejectButtonTooltip"
        :type="'suggestion-decline hover:bg-hover-onHover py-1 px-2'"
        :button-dimensions="{ height: 24 }"
        :custom-icon-name="'x'"
        :custom-icon-size="'16px'"
        :custom-icon-color="'var(--destructiveIcon)'"
        :text="translate('reject')"
        @click="reject"
      />
      <ButtonCustom
        v-tooltip="acceptButtonTooltip"
        :type="'suggestion-accept hover:bg-hover-onHover py-1 px-2'"
        :button-dimensions="{ height: 24 }"
        :custom-icon-name="acceptButtonIconName"
        :custom-icon-size="'16px'"
        :custom-icon-color="'var(--positiveIcon)'"
        :text="acceptButtonText"
        @click="accept"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import CompactCard from '@/components/widgets/CompactCard';
import NoteSuggestedActionDetails from '@/components/notes/NoteSuggestedActionDetails';
import useSuggestions from '@/utilities/composables/useSuggestions';
import useTranslate from '@/utilities/composables/useTranslate';
import pickBy from 'lodash/pickBy';
import isEmpty from 'lodash/isEmpty';
import ButtonCustom from '@/components/elements/ButtonCustom';
import useSuggestedActions from '@/utilities/composables/useSuggestedActions';

const { suggestionTooltip } = useSuggestions();
const { translate } = useTranslate();
const { actionTypeDefinitions, actionTypes } = useSuggestedActions();

const props = defineProps({
  action: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['accept', 'reject']);

const DEFAULT_TOOLTIP_OPTIONS = {
  placement: 'top',
  offset: [0, 4],
};

const actionType = computed(() => {
  return props.action.type || Object.keys(actionTypes).find((key) => props.action[key]) || '';
});

const actionDetails = computed(() => {
  return props.action[actionType.value] || {};
});

const actionResourceTitle = computed(() => {
  return actionDetails.value.title || '';
});

const isCreateCardAction = computed(() => {
  return actionType.value === actionTypes.create_card;
});

const actionTypeProperties = computed(() => {
  return actionTypeDefinitions[actionType.value] || {};
});

const actionName = computed(() => {
  const action = actionTypeProperties.value.headerTitleKey || '';
  return action ? `${translate(action)}` : '';
});

const actionCardId = computed(() => {
  return actionDetails.value.card_id || '';
});

// Filter out empty or null values and also filter out card_id
const validActionDetails = computed(() => {
  const details = actionDetails.value;
  if (!details || typeof details !== 'object') return {};

  return pickBy(details, (value, key) => {
    return !isEmpty(value) && key !== 'card_id';
  });
});

const acceptButtonText = computed(() => {
  return translate(actionTypeProperties.value.acceptButtonKey ?? 'accept');
});

const acceptButtonIconName = computed(() => {
  return actionTypeProperties.value.acceptButtonIcon ?? 'check';
});

const rejectButtonTooltip = computed(() => {
  return { ...suggestionTooltip('reject'), ...DEFAULT_TOOLTIP_OPTIONS };
});

const acceptButtonTooltip = computed(() => {
  return { ...suggestionTooltip('accept'), ...DEFAULT_TOOLTIP_OPTIONS };
});

const accept = (event: any) => {
  const dataForEmit = {
    ...props.action,
    [actionType.value]: pickBy(actionDetails.value, (value) => !isEmpty(value)),
  };
  emit('accept', dataForEmit, event);
};

const reject = (event: any) => {
  emit('reject', props.action, event);
};
</script>

<style lang="scss">
$borderWidth: 1px;
$borderRadius: 8px;
.note-suggested-action-wrap {
  @apply flex flex-col w-full box-border bg-standard-bg text-sm;
  // border radius of elements with border
  border-radius: calc(#{$borderRadius} + #{$borderWidth});

  &:hover {
    @apply bg-important-hover;
  }

  // each child element, but not inner children of the child
  > * {
    @apply border-solid border-standard-border;
    border-width: $borderWidth;
  }

  .note-suggested-action-header,
  .note-suggested-action-footer {
    @apply flex;
  }

  .note-suggested-action-header {
    @apply border-b-0 px-3 py-2;
    border-top-left-radius: $borderRadius;
    border-top-right-radius: $borderRadius;

    .suggested-action-header-breadcrumb {
      @apply flex items-center gap-1 max-w-full text-text-default;
    }
  }

  .note-suggested-action-footer {
    @apply justify-between border-t-0 p-2;
    border-bottom-left-radius: $borderRadius;
    border-bottom-right-radius: $borderRadius;

    .button-wrap {
      border: 1px solid;
      @apply rounded;

      .button-icon {
        @apply mr-1;
      }

      .button-label {
        @apply text-sm;
      }

      &.suggestion-accept {
        @apply bg-transparent;
        border-color: var(--positiveBorder);
        .button-label {
          color: var(--positiveText);
        }
      }

      &.suggestion-decline {
        @apply bg-transparent;
        border-color: var(--destructiveBorder);

        .button-label {
          color: var(--destructiveText);
        }
      }
    }
  }

  .note-suggested-action {
    @apply flex items-center p-3 box-border;
  }
}
</style>
